// Spools tab panel: header bar (filter chips + sort + actions),
// KPI strip, and the spool table itself. Filter + sort live here
// because they're scoped to this single job, not the FilterBar URL
// state at the page level.
import { useMemo, useState } from "react";
import type {
  DrawingDto,
  JobDto,
  SolutionTypeDto,
  SpoolDto,
  SpoolStageDto,
  StageKey,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { SpoolTable, type SpoolDraft } from "../components/SpoolTable";
import { DistributeNominalButton } from "./DistributeNominalButton";

export type SpoolFilter = "all" | "open" | "at-risk" | "overdue" | "complete";

interface Props {
  job: JobDto | null | undefined;
  spools: SpoolDraft[];
  onSpoolsChange(next: SpoolDraft[]): void;
  team: TeamMemberDto[];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  types?: SolutionTypeDto[];
  focusSpoolId?: number | null;
  // Same value as focusSpoolId on the cross-nav path: when the user
  // clicks a match chip in the Drawings tab, the spool not only
  // scrolls into view but its stage panel expands too. Workshop
  // wants one click → all the spool's detail.
  expandSpoolId?: number | null;
  stagesBySpool: Map<number, SpoolStageDto[]>;
  weldBayBySpool: Map<number, StageKey | null>;
  // Map of spool id → its linked drawing (workshop convention is 1:1).
  // Powers the per-spool "📐 Drawings" button on SpoolCard.
  drawingBySpoolId?: Map<number, DrawingDto>;
  onStageChanged?: () => void;
  onImport(): void;
  onAutoSchedule?: () => void;
  autoScheduleBusy?: boolean;
  jobStartDate?: string;
  // Live nominal labour budget from the Details form (not the saved
  // job row) so "Distribute nominal hrs" acts on what the estimator
  // just typed without forcing a Save first.
  nominalStageHours?: Record<string, number>;
  // Cross-nav: switch the modal to the Drawings tab.
  onOpenDrawings?: () => void;
}

type SortMode = "custom" | "num" | "hours-desc" | "stages-incomplete";

export function SpoolsTabPanel({
  job,
  spools,
  onSpoolsChange,
  team,
  focusSpoolId,
  expandSpoolId,
  stagesBySpool,
  weldBayBySpool,
  drawingBySpoolId,
  onStageChanged,
  onImport,
  onAutoSchedule,
  autoScheduleBusy,
  nominalStageHours,
  onOpenDrawings,
}: Props) {
  const [filter, setFilter] = useState<SpoolFilter>("all");
  const [sort, setSort] = useState<SortMode>("custom");
  const [query, setQuery] = useState<string>("");
  const [layout, setLayout] = useState<"cards" | "table">("cards");
  const persisted = typeof job?.id === "number";

  // Filter operates on the persisted spools (JobDto), then maps back
  // to the draft list via id. Drafts without an id (newly added,
  // unsaved) always render so the user doesn't lose work.
  const { kpis, visibleIds } = useMemo(
    () => bucketise(job?.spools ?? [], filter),
    [job?.spools, filter],
  );

  // Drift between the saved spool list and the draft list. The
  // nominal-hours split runs server-side against the saved rows, so
  // the confirm dialog names both directions: drafts with no id yet
  // get no share, and saved spools dropped from the list still take
  // one (leaving the rest short once the removal is saved). Counted
  // off the full draft list, not the filtered view.
  const spoolDrift = useMemo(() => {
    const draftIds = new Set(
      spools.map((d) => d.id).filter((id): id is number => typeof id === "number"),
    );
    return {
      unsaved: spools.filter((d) => typeof d.id !== "number").length,
      removed: (job?.spools ?? []).filter((s) => !draftIds.has(s.id)).length,
    };
  }, [spools, job?.spools]);

  const visibleDrafts = useMemo(() => {
    const filtered = filter === "all"
      ? spools
      : spools.filter((d) => typeof d.id !== "number" || visibleIds.has(d.id));
    const q = query.trim().toLowerCase();
    const searched = q === ""
      ? filtered
      : filtered.filter((d) => {
          if (d.num.toLowerCase().includes(q)) return true;
          if ((d.description ?? "").toLowerCase().includes(q)) return true;
          return false;
        });
    return applySort(searched, sort, stagesBySpool);
  }, [spools, filter, visibleIds, sort, stagesBySpool, query]);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        <LayoutToggle layout={layout} onChange={setLayout} />
        <span style={{ width: 1, height: 22, background: "var(--bsp-border)", margin: "0 2px" }} />
        <FilterChips active={filter} onChange={setFilter} kpis={kpis} disabled={!persisted} />
        <div style={{ flex: 1 }} />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search spool # or description…"
          aria-label="Search spools"
          style={{
            padding: "4px 8px",
            fontSize: 11,
            border: "1px solid var(--bsp-border-strong)",
            borderRadius: 4,
            fontFamily: "var(--bsp-font-sans)",
            minWidth: 200,
          }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: "var(--bsp-fg-muted)" }}>
          Sort:
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortMode)}
            style={{ padding: "4px 8px", fontSize: 11, border: "1px solid var(--bsp-border-strong)", borderRadius: 4, fontFamily: "var(--bsp-font-sans)" }}
          >
            <option value="custom">Custom (drag)</option>
            <option value="num">Spool #</option>
            <option value="hours-desc">Hours (high → low)</option>
            <option value="stages-incomplete">Incomplete first</option>
          </select>
        </label>
        {persisted && typeof job?.id === "number" && (
          <DistributeNominalButton
            jobId={job.id}
            spoolCount={job.spools.length}
            unsavedSpoolCount={spoolDrift.unsaved}
            removedSpoolCount={spoolDrift.removed}
            nominalStageHours={nominalStageHours ?? {}}
            onDistributed={onStageChanged}
          />
        )}
        {persisted && onAutoSchedule && (
          <button
            type="button"
            className="btn-sm"
            onClick={onAutoSchedule}
            disabled={autoScheduleBusy}
            style={{ background: "var(--bsp-blue)", color: "#fff", borderColor: "var(--bsp-blue)" }}
            title="Cascade hours through every spool's stages from the job start date"
          >
            {autoScheduleBusy ? "Scheduling…" : "⚡ Auto-schedule"}
          </button>
        )}
        <button
          type="button"
          className="btn-sm"
          onClick={onImport}
          style={{ background: "var(--bsp-blue-tint)", borderColor: "var(--bsp-blue)", color: "var(--bsp-blue)" }}
        >
          ⇪ Import CSV
        </button>
      </div>
      {persisted && <KpiStrip kpis={kpis} />}
      <SpoolTable
        spools={visibleDrafts}
        onChange={(next) => {
          // When a filter is active, the spool table only sees the
          // filtered subset. Re-merge with hidden drafts on save so we
          // don't drop rows that weren't visible during the edit.
          if (filter === "all") {
            onSpoolsChange(next);
            return;
          }
          const visibleKeys = new Set(next.map((d) => d.localKey));
          const merged: SpoolDraft[] = [];
          const replaced = new Map(next.map((d) => [d.localKey, d]));
          for (const orig of spools) {
            if (replaced.has(orig.localKey)) {
              merged.push(replaced.get(orig.localKey)!);
            } else if (!visibleKeys.has(orig.localKey)) {
              merged.push(orig);
            }
          }
          // New drafts (no original counterpart) appended at end.
          for (const d of next) {
            if (!spools.some((s) => s.localKey === d.localKey)) merged.push(d);
          }
          onSpoolsChange(merged);
        }}
        jobId={job?.id}
        jobNumber={job?.jobNumber}
        focusSpoolId={focusSpoolId ?? null}
        expandSpoolId={expandSpoolId ?? null}
        team={persisted ? team : undefined}
        stagesBySpool={stagesBySpool}
        weldBayBySpool={weldBayBySpool}
        jobBay={job?.bay ?? null}
        drawingBySpoolId={drawingBySpoolId}
        onStageChanged={onStageChanged}
        onOpenDrawings={onOpenDrawings}
        layout={layout}
      />
    </>
  );
}

// Cards | Table segmented control at the left of the Spools toolbar.
function LayoutToggle({ layout, onChange }: { layout: "cards" | "table"; onChange: (l: "cards" | "table") => void }) {
  const seg = (id: "cards" | "table", label: string) => {
    const active = layout === id;
    return (
      <button
        type="button"
        onClick={() => onChange(id)}
        style={{
          padding: "5px 11px",
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          border: "1px solid var(--bsp-border)",
          background: active ? "var(--bsp-blue)" : "var(--bsp-surface)",
          color: active ? "#fff" : "var(--bsp-fg)",
          borderColor: active ? "var(--bsp-blue)" : "var(--bsp-border)",
          borderRadius: 4,
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {seg("cards", "Cards")}
      {seg("table", "Table")}
    </div>
  );
}

// View-only sort. The underlying spools array keeps its drag-set
// sortOrder so saving + dragging always reflect the user's intent;
// the sort dropdown only changes the rendered order. When set to
// custom we hand back the input unchanged.
function applySort(
  spools: SpoolDraft[],
  mode: SortMode,
  stagesBySpool?: Map<number, import("../../../../../../shared/types/production-schedule").SpoolStageDto[]>,
): SpoolDraft[] {
  if (mode === "custom") return spools;
  const copy = spools.slice();
  if (mode === "num") {
    return copy.sort((a, b) => a.num.localeCompare(b.num, "en", { numeric: true, sensitivity: "base" }));
  }
  if (mode === "hours-desc") {
    return copy.sort((a, b) => b.hours - a.hours);
  }
  if (mode === "stages-incomplete") {
    return copy.sort((a, b) => {
      const ac = remainingActiveStages(a, stagesBySpool);
      const bc = remainingActiveStages(b, stagesBySpool);
      // More incomplete stages first; ties fall back to sortOrder.
      return bc - ac || a.sortOrder - b.sortOrder;
    });
  }
  return spools;
}

function remainingActiveStages(
  s: SpoolDraft,
  stagesBySpool?: Map<number, import("../../../../../../shared/types/production-schedule").SpoolStageDto[]>,
): number {
  if (typeof s.id !== "number") return Number.MAX_SAFE_INTEGER; // brand new drafts: treat as fully incomplete
  const stages = stagesBySpool?.get(s.id) ?? [];
  let n = 0;
  for (const st of stages) {
    if (st.status === "skipped") continue;
    if (st.status !== "complete") n += 1;
  }
  return n;
}

interface BucketKpis {
  all: number;
  open: number;
  atRisk: number;
  overdue: number;
  complete: number;
  totalStages: number;
  totalHours: number;
}

function bucketise(spools: SpoolDto[], filter: SpoolFilter): {
  kpis: BucketKpis;
  visibleIds: Set<number>;
} {
  const todayIso = new Date().toISOString().slice(0, 10);
  const sevenDays = new Date();
  sevenDays.setDate(sevenDays.getDate() + 7);
  const sevenIso = sevenDays.toISOString().slice(0, 10);
  let totalStages = 0;
  let totalHours = 0;
  let atRisk = 0;
  let overdue = 0;
  let complete = 0;
  let open = 0;
  const visibleIds = new Set<number>();
  for (const sp of spools) {
    const activeStages = sp.stages.filter((s) => s.status !== "skipped");
    totalStages += activeStages.length;
    for (const st of activeStages) totalHours += st.hours;
    const allDone = activeStages.length > 0 && activeStages.every((s) => s.status === "complete");
    const due = sp.weldDone ?? sp.cutDone ?? sp.weldStart ?? sp.cutStart ?? null;
    const isOverdue = !!due && due < todayIso && !allDone;
    const isAtRisk = !isOverdue && !!due && due <= sevenIso && !allDone;
    let bucket: SpoolFilter;
    if (allDone) { complete += 1; bucket = "complete"; }
    else if (isOverdue) { overdue += 1; bucket = "overdue"; }
    else if (isAtRisk) { atRisk += 1; bucket = "at-risk"; }
    else { bucket = "open"; }
    if (!allDone) open += 1;
    if (filter === "all" || filter === bucket) visibleIds.add(sp.id);
  }
  return {
    kpis: {
      all: spools.length,
      open,
      atRisk,
      overdue,
      complete,
      totalStages,
      totalHours,
    },
    visibleIds,
  };
}

function FilterChips({
  active,
  onChange,
  kpis,
  disabled,
}: {
  active: SpoolFilter;
  onChange: (f: SpoolFilter) => void;
  kpis: BucketKpis;
  disabled: boolean;
}) {
  const chips: Array<{ id: SpoolFilter; label: string; count: number; color?: string }> = [
    { id: "all", label: "All", count: kpis.all },
    { id: "open", label: "Open", count: kpis.open },
    { id: "at-risk", label: "At risk", count: kpis.atRisk, color: "#C77700" },
    { id: "overdue", label: "Overdue", count: kpis.overdue, color: "var(--bsp-red-fg)" },
    { id: "complete", label: "Complete", count: kpis.complete, color: "var(--bsp-green-fg, #2E7D32)" },
  ];
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {chips.map((c) => {
        const isActive = active === c.id;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(c.id)}
            disabled={disabled}
            style={{
              padding: "5px 11px",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              border: "1px solid var(--bsp-border)",
              background: isActive ? "var(--bsp-blue)" : "var(--bsp-surface)",
              color: isActive ? "#fff" : c.color ?? "var(--bsp-fg)",
              borderColor: isActive ? "var(--bsp-blue)" : "var(--bsp-border)",
              borderRadius: 4,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.55 : 1,
            }}
          >
            {c.label}
            <span style={{ marginLeft: 5, opacity: 0.85 }}>{c.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function KpiStrip({ kpis }: { kpis: BucketKpis }) {
  const items: Array<{ label: string; value: string; tone?: "ok" | "warn" | "bad" }> = [
    { label: "Spools", value: String(kpis.all) },
    { label: "Stages", value: String(kpis.totalStages) },
    { label: "Workshop hrs", value: `${kpis.totalHours.toFixed(0)} h` },
    { label: "On track", value: String(Math.max(0, kpis.all - kpis.atRisk - kpis.overdue - kpis.complete)), tone: "ok" },
    { label: "At risk", value: String(kpis.atRisk), tone: kpis.atRisk > 0 ? "warn" : undefined },
    { label: "Overdue", value: String(kpis.overdue), tone: kpis.overdue > 0 ? "bad" : undefined },
  ];
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(6, 1fr)",
      gap: 10,
      padding: "10px 12px",
      marginBottom: 10,
      background: "var(--bsp-surface-muted)",
      border: "1px solid var(--bsp-border)",
      borderRadius: 4,
    }}>
      {items.map((it) => (
        <div key={it.label}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--bsp-fg-muted)" }}>
            {it.label}
          </div>
          <div style={{
            fontSize: 22, fontWeight: 800, lineHeight: 1.1,
            color: it.tone === "ok" ? "var(--bsp-green-fg, #2E7D32)"
              : it.tone === "warn" ? "#C77700"
              : it.tone === "bad" ? "var(--bsp-red-fg)"
              : "var(--bsp-fg)",
            fontFamily: "var(--bsp-font-mono)",
          }}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}
